import React, { FunctionComponent } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import UserInfo from '@app/components/sidebar/UserInfo';
import LogoImage from '@app/images/nsknox_logo.svg';
import SVG from '@app/components/SVG';
import { Link } from 'react-router-dom';
import { groupBy } from '@app/utils/utils';
import SectionCategory from '@app/components/sidebar/SectionCategory';
import useInfraStores from '@app/hooks/useInfraStores';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
  className?: string;
}

const Sidebar: FunctionComponent<HeaderProps> = observer((props) => {
  const { navigationStore, permissionsStore } = useInfraStores();
  const { t } = useTranslation();

  const { selectedSection } = navigationStore;

  const sectionsByCategory = groupBy(permissionsStore.visibleSections, (section) => section.category);

  return (
    <BackgroundContainer className={props.className}>
      <LogoTitle to={{ pathname: navigationStore.initialRoutePath }}>
        <StyledLogo accessibilityLabel={t('general.accessibility.homeButton')} image={LogoImage} />
      </LogoTitle>
      <Sections>
        {Array.from(sectionsByCategory.entries()).map(([sectionCategory, sectionsOfCategory]) => (
          <StyledSectionCategory
            key={sectionCategory ?? ''}
            category={sectionCategory}
            sections={sectionsOfCategory}
            selectedSectionId={selectedSection?.id}
          />
        ))}
      </Sections>
      <StyledUserInfo />
    </BackgroundContainer>
  );
});

export default Sidebar;

const BackgroundContainer = styled.aside`
  background-color: #132361;
  z-index: 10;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 200px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-feature-settings: normal;
  font-variant: normal;
`;

const LogoTitle = styled(Link)`
  flex: 0 0 auto;
  align-self: stretch;
  display: flex;
  align-items: center;
  padding: 20px 16px 24px 16px;
`;

const Sections = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const StyledSectionCategory = styled(SectionCategory)`
  padding: 32px 8px;
  border-top: 1px solid #ffffff26;
`;

const StyledLogo = styled(SVG)`
  height: 24px;
  width: 97px;
`;

const StyledUserInfo = styled(UserInfo)`
  flex: 0 0 auto;
  border-top: 1px solid #ffffff26;
  padding: 16px;
`;
